import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { FileText, Upload, Trash, X, Plus, Folder, Warning, Check, Shield, Users as UsersIcon, CalendarCheck, FirstAidKit, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import MyDocumentsPage from './MyDocumentsPage';
import EmployeesPage from './EmployeesPage';
import ValidateHoursPage from './ValidateHoursPage';
import VerticalTabs, { useSearch } from '../components/VerticalTabs';
import { AdminDocsTab } from '../components/documents/AdminDocsTab';
import { fmtDateFR } from '../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentsPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const isAdmin = user?.role === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const [mainTab, setMainTab] = useState(urlTab || 'salaries_merged');
  useEffect(() => { if (urlTab && urlTab !== mainTab) setMainTab(urlTab); }, [urlTab, mainTab]);
  const handleTabChange = (v) => { setMainTab(v); setSearchParams({ tab: v }, { replace: true }); };

  if (!isAdmin) return <MyDocumentsPage />;

  const TABS = [
    { key: 'salaries_merged', label: 'Salaries', icon: UsersIcon },
    { key: 'conges', label: 'Conges', icon: CalendarCheck },
    { key: 'maladie', label: 'Arrets & Absences', icon: FirstAidKit },
    { key: 'validate', label: 'Validation heures', icon: CheckCircle },
    { key: 'company_docs', label: 'Docs obligatoires', icon: Shield },
    { key: 'employee_docs', label: 'Docs salaries', icon: Folder },
    { key: 'admin_docs', label: 'Archives', icon: Folder },
  ];

  return (
    <div data-testid="administration-page">
      <div className="page-header">
        <h1 className="page-title">{t('page.documents')}</h1>
        <p className="page-subtitle">{t('page.documents_sub')}</p>
      </div>

      <VerticalTabs tabs={TABS} activeTab={mainTab} onTabChange={handleTabChange}>
        {mainTab === 'salaries_merged' && <EmployeesPage embeddedTab="salaries_merged" />}
        {mainTab === 'conges' && <EmployeesPage embeddedTab="conges" />}
        {mainTab === 'maladie' && <EmployeesPage embeddedTab="maladie" />}
        {mainTab === 'validate' && <ValidateHoursPage embedded />}
        {mainTab === 'company_docs' && <CompanyDocsTab />}
        {mainTab === 'employee_docs' && <MyDocumentsPage />}
        {mainTab === 'admin_docs' && <AdminDocsTab />}
      </VerticalTabs>
    </div>
  );
};


// Company obligatory documents tab
const CompanyDocsTab = () => {
  const [docTypes, setDocTypes] = useState([]);
  const [companyDocs, setCompanyDocs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadKey, setUploadKey] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [validityDate, setValidityDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [isUpdate, setIsUpdate] = useState(false);
  const { searchTerm } = useSearch();
  const filteredDocTypes = docTypes.filter(dt => {
    if (!searchTerm) return true;
    return dt.label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const fetchData = useCallback(async () => {
    try {
      const [typesRes, docsRes, alertsRes] = await Promise.all([
        axios.get(`${API_URL}/api/company-docs/types`, { withCredentials: true }),
        axios.get(`${API_URL}/api/company-docs`, { withCredentials: true }),
        axios.get(`${API_URL}/api/company-docs/expiry-alerts`, { withCredentials: true }),
      ]);
      setDocTypes(typesRes.data);
      setCompanyDocs(docsRes.data);
      setAlerts(alertsRes.data.alerts);
    } catch (e) { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadKey) return;
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('doc_key', uploadKey);
    formData.append('validity_date', validityDate);
    formData.append('expiry_date', expiryDate);
    formData.append('notes', notes);
    formData.append('is_update', isUpdate ? 'true' : 'false');
    try {
      await axios.post(`${API_URL}/api/company-docs`, formData, { withCredentials: true });
      toast.success('Document ajoute');
      setShowUpload(false); setUploadFile(null); setValidityDate(''); setExpiryDate(''); setNotes(''); setIsUpdate(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur upload'); }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`${API_URL}/api/company-docs/${docId}`, { withCredentials: true });
      toast.success('Document supprime');
      fetchData();
    } catch (e) { toast.error('Erreur suppression'); }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  const getDocStatus = (latest) => {
    if (!latest) return <span style={{ color: '#C62828', fontWeight: '700' }}>Manquant</span>;
    if (latest.is_expired) return <span style={{ color: '#C62828', fontWeight: '700' }}>Expire</span>;
    if (latest.expires_soon) return <span style={{ color: '#E65100', fontWeight: '700' }}>Bientot expire</span>;
    return <span style={{ color: '#2E7D32', fontWeight: '700' }}><Check size={14} /> Valide</span>;
  };

  const getExpiryColor = (latest) => {
    if (latest?.is_expired) return '#C62828';
    if (latest?.expires_soon) return '#E65100';
    return '#2E7D32';
  };

  return (
    <div data-testid="company-docs-tab">
      {alerts.length > 0 && (
        <div className="card" style={{ marginBottom: '24px', padding: '16px' }}>
          <h4 style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: '8px', color: '#C62828' }}>
            <Warning size={20} weight="fill" /> Alertes documents ({alerts.length})
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {alerts.map((a, i) => (
              <div key={`alert-${a.doc_key || ''}-${i}`} data-testid={`doc-alert-${i}`} style={{
                padding: '10px 14px', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600',
                backgroundColor: a.severity === 'danger' ? '#FFEBEE' : '#FFF3E0',
                color: a.severity === 'danger' ? '#C62828' : '#E65100',
                borderLeft: `4px solid ${a.severity === 'danger' ? '#EF5350' : '#FF9800'}`
              }}>
                {a.message}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <button className="btn btn-primary" onClick={() => setShowUpload(true)} data-testid="add-company-doc-btn">
          <Plus size={16} /> Ajouter un document
        </button>
      </div>

      <div className="card">
        <table className="data-table" data-testid="company-docs-table">
          <thead><tr><th>Document</th><th>Fichier</th><th>Date de validite</th><th>Date d'expiration</th><th>Statut</th><th>Actions</th></tr></thead>
          <tbody>
            {filteredDocTypes.map(dt => {
              const typeDocs = companyDocs.filter(d => d.doc_key === dt.key);
              const latest = typeDocs.find(d => !d.is_update) || typeDocs[0];
              const updates = typeDocs.filter(d => d.is_update);
              return (
                <React.Fragment key={dt.key}>
                  <tr>
                    <td style={{ fontWeight: '700' }}>{dt.label}</td>
                    <td>{latest ? (<a href={`${API_URL}/api/company-docs/file/${latest.filename}`} target="_blank" rel="noreferrer" style={{ color: '#1565C0', textDecoration: 'underline', fontSize: '0.8rem' }}>{latest.original_filename}</a>) : <span style={{ color: '#C62828', fontWeight: '600', fontSize: '0.8rem' }}>MANQUANT</span>}</td>
                    <td style={{ fontSize: '0.85rem' }}>{latest?.validity_date || '-'}</td>
                    <td style={{ fontSize: '0.85rem', fontWeight: '600', color: getExpiryColor(latest) }}>
                      {latest?.expiry_date || '-'}
                      {latest?.is_expired && <span style={{ marginLeft: '6px', fontSize: '0.7rem' }}>EXPIRE</span>}
                      {latest?.expires_soon && <span style={{ marginLeft: '6px', fontSize: '0.7rem' }}>BIENTOT</span>}
                    </td>
                    <td>{getDocStatus(latest)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button className="btn btn-sm btn-primary" onClick={() => { setUploadKey(dt.key); setIsUpdate(false); setShowUpload(true); }} data-testid={`upload-doc-${dt.key}`}><Upload size={12} /> Televerser</button>
                        {(dt.key === 'statuts' || dt.key === 'pv_ag') && (<button className="btn btn-sm btn-secondary" onClick={() => { setUploadKey(dt.key); setIsUpdate(true); setShowUpload(true); }} data-testid={`update-doc-${dt.key}`}><Plus size={12} /> Mise a jour</button>)}
                        {latest && (<button className="btn btn-sm btn-secondary" style={{ color: '#C62828' }} onClick={() => handleDelete(latest.id)}><Trash size={12} /></button>)}
                      </div>
                    </td>
                  </tr>
                  {updates.map(u => (
                    <tr key={u.id} style={{ backgroundColor: '#FAFBFF' }}>
                      <td style={{ paddingLeft: '32px', fontSize: '0.8rem', color: '#5E5F66' }}>Mise a jour</td>
                      <td><a href={`${API_URL}/api/company-docs/file/${u.filename}`} target="_blank" rel="noreferrer" style={{ color: '#1565C0', fontSize: '0.8rem' }}>{u.original_filename}</a></td>
                      <td colSpan={2} style={{ fontSize: '0.8rem' }}>{fmtDateFR(u.uploaded_at)}</td>
                      <td></td>
                      <td><button className="btn btn-sm btn-secondary" style={{ color: '#C62828' }} onClick={() => handleDelete(u.id)}><Trash size={12} /></button></td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {showUpload && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>{isUpdate ? 'Mise a jour du document' : 'Ajouter un document'}</h3>
              <button onClick={() => { setShowUpload(false); setUploadFile(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
            </div>
            <form onSubmit={handleUpload} style={{ padding: '24px' }}>
              {!uploadKey && (
                <div className="form-group">
                  <label className="form-label">Type de document *</label>
                  <select className="form-input" value={uploadKey} onChange={(e) => setUploadKey(e.target.value)} required>
                    <option value="">Selectionner...</option>
                    {docTypes.map(dt => <option key={dt.key} value={dt.key}>{dt.label}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group"><label className="form-label">Fichier *</label><input type="file" className="form-input" onChange={(e) => setUploadFile(e.target.files[0])} required data-testid="company-doc-file-input" /></div>
              <div className="form-group"><label className="form-label">Date du document</label><input type="date" className="form-input" value={validityDate} onChange={(e) => setValidityDate(e.target.value)} data-testid="validity-date-input" /></div>
              <div className="form-group"><label className="form-label">Date d'expiration *</label><input type="date" className="form-input" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} data-testid="expiry-date-input" /></div>
              <div className="form-group"><label className="form-label">Notes</label><input type="text" className="form-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Commentaires..." /></div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowUpload(false); setUploadFile(null); }}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={!uploadFile} data-testid="submit-company-doc-btn">Ajouter</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;
